
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Typography,
    Container,
    Paper,
    Autocomplete,
    TextField,
    CircularProgress,
    Box,
    Card,
    CardContent,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Divider
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SavingsIcon from '@mui/icons-material/Savings';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { getPriceAlerts, getItens, getItemPriceHistory, getMunicipios, getObras } from '../services/api';
import { format, parseISO, startOfDay, endOfDay, subMonths, subYears } from 'date-fns';
import { Button, ButtonGroup } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// --- Type Definitions ---
interface AlertaPreco {
    id: number;
    descricao: string;
    ultimo_preco_liquido: number;
    preco_medio_liquido: number;
    variacao_percentual: number;
    obra_id: number;
    obra_nome: string;
    fornecedor_id: number | null;
    fornecedor_nome: string | null;
}

interface CatalogoItem {
    id: number;
    descricao: string;
}

interface HistoricoPrecoItem {
    lancamento_id: number;
    obra_id: number;
    fornecedor_id: number | null;
    data_lancamento: string;
    obra_nome: string;
    municipio: string;
    fornecedor_nome: string;
    quantidade: number;
    valor_bruto_unitario: number;
    valor_desconto_unitario: number;
    valor_liquido_unitario: number;
}

// --- Helper Functions ---
const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const formatPercentage = (value: number) => {
    const signal = value > 0 ? '+' : '';
    return signal + value.toFixed(2) + '%';
};

const formatDate = (dateString: string) => {
    if (!dateString) return '';
    // Format the date string directly, interpreting it as UTC to avoid timezone shifts.
    return new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(new Date(dateString));
};


// --- Main Component ---
const MemoriaPrecoPage: React.FC = () => {


    const [selectedItem, setSelectedItem] = useState<CatalogoItem | null>(null);
    const [selectedMunicipio, setSelectedMunicipio] = useState<string>('Todos');
    const [selectedObraId, setSelectedObraId] = useState<string | number>('Todos');
    const [selectedFornecedorId, setSelectedFornecedorId] = useState<string | number>('Todos');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [alertThreshold, setAlertThreshold] = useState<number>(10);

    // React Query hooks for initial data
    const { data: alertas, isLoading: loadingAlertas } = useQuery({
        queryKey: ['price-alerts', alertThreshold],
        queryFn: () => getPriceAlerts(alertThreshold),
    });

    const { data: catalogoItens = [] } = useQuery({
        queryKey: ['catalogo-itens'],
        queryFn: () => getItens(),
    });

    const { data: municipiosData = [] } = useQuery({
        queryKey: ['municipios'],
        queryFn: () => getMunicipios(),
    });

    const { data: obrasData = [] } = useQuery({
        queryKey: ['obras'],
        queryFn: () => getObras(),
    });

    // Derived state for dropdowns
    const municipios = useMemo(() => ['Todos', ...municipiosData], [municipiosData]);
    const obrasList = useMemo(() => obrasData, [obrasData]);

    // React Query hook for price history (dependent query)
    const { data: priceHistory = [], isLoading: loadingHistory } = useQuery({
        queryKey: ['item-price-history', selectedItem?.id, selectedMunicipio],
        queryFn: () => getItemPriceHistory(selectedItem!.id, selectedMunicipio),
        enabled: !!selectedItem,
    });

    // Derived list of unique fornecedores from price history (only shows relevant ones for this item)
    const fornecedoresList = useMemo(() => {
        const map = new Map<number, string>();
        priceHistory.forEach((item: HistoricoPrecoItem) => {
            if (item.fornecedor_id && item.fornecedor_nome) {
                map.set(item.fornecedor_id, item.fornecedor_nome);
            }
        });
        return Array.from(map.entries()).map(([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome));
    }, [priceHistory]);

    // Memoized calculations for metrics with filters
    const filteredHistory = useMemo(() => {
        return priceHistory.filter((item: HistoricoPrecoItem) => {
            // Filter by Obra
            if (selectedObraId !== 'Todos' && item.obra_id !== Number(selectedObraId)) {
                return false;
            }

            // Filter by Fornecedor
            if (selectedFornecedorId !== 'Todos' && item.fornecedor_id !== Number(selectedFornecedorId)) {
                return false;
            }

            // Filter by Date
            if (startDate || endDate) {
                const itemDate = parseISO(item.data_lancamento);
                if (startDate && itemDate < startOfDay(parseISO(startDate))) return false;
                if (endDate && itemDate > endOfDay(parseISO(endDate))) return false;
            }

            return true;
        });
    }, [priceHistory, selectedObraId, selectedFornecedorId, startDate, endDate]);

    const metrics = useMemo(() => {
        if (filteredHistory.length === 0) {
            return { ultimo: 0, medio: 0, maisAlto: 0, maisBaixo: 0, variacao: 0, economiaPotencial: 0 };
        }

        // Sort by date to ensure we get the actual most recent price after filtering
        const sortedHistory = [...filteredHistory].sort((a, b) => {
            const dateComparison = new Date(b.data_lancamento).getTime() - new Date(a.data_lancamento).getTime();
            if (dateComparison !== 0) return dateComparison;
            return b.lancamento_id - a.lancamento_id;
        });

        const netPrices = sortedHistory.map(h => h.valor_liquido_unitario);
        const ultimo = netPrices[0];
        const medio = netPrices.reduce((a, b) => a + b, 0) / netPrices.length;
        const maisAlto = Math.max(...netPrices);
        const maisBaixo = Math.min(...netPrices);
        const variacao = medio > 0 ? ((ultimo - medio) / medio) * 100 : 0;

        // Economia potencial: quanto teria sido economizado se todas as compras fossem pelo menor preço
        const economiaPotencial = filteredHistory.reduce((acc, h) => {
            return acc + (h.valor_liquido_unitario - maisBaixo) * h.quantidade;
        }, 0);

        return { ultimo, medio, maisAlto, maisBaixo, variacao, economiaPotencial };
    }, [filteredHistory]);

    const chartData = useMemo(() => {
        const reversed = [...filteredHistory].reverse();
        const formatted = reversed.map((h, i) => ({
            ...h,
            data_lancamento: formatDate(h.data_lancamento).slice(0, 5),
            _index: i,
        }));

        // Linear regression for trend line
        if (formatted.length >= 2) {
            const n = formatted.length;
            const sumX = formatted.reduce((s, _, i) => s + i, 0);
            const sumY = formatted.reduce((s, d) => s + d.valor_liquido_unitario, 0);
            const sumXY = formatted.reduce((s, d, i) => s + i * d.valor_liquido_unitario, 0);
            const sumX2 = formatted.reduce((s, _, i) => s + i * i, 0);
            const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
            const intercept = (sumY - slope * sumX) / n;
            return formatted.map((d, i) => ({ ...d, tendencia: intercept + slope * i }));
        }
        return formatted.map(d => ({ ...d, tendencia: d.valor_liquido_unitario }));
    }, [filteredHistory]);

    // Ranking of fornecedores by average net price for the selected item
    const fornecedorRanking = useMemo(() => {
        if (filteredHistory.length === 0) return [];
        const map = new Map<number, { nome: string; prices: number[]; lastDate: string }>();
        filteredHistory.forEach((item: HistoricoPrecoItem) => {
            if (!item.fornecedor_id || !item.fornecedor_nome) return;
            const entry = map.get(item.fornecedor_id) || { nome: item.fornecedor_nome, prices: [], lastDate: '' };
            entry.prices.push(item.valor_liquido_unitario);
            if (!entry.lastDate || item.data_lancamento > entry.lastDate) {
                entry.lastDate = item.data_lancamento;
            }
            map.set(item.fornecedor_id, entry);
        });
        return Array.from(map.entries())
            .map(([id, data]) => ({
                id,
                nome: data.nome,
                precoMedio: data.prices.reduce((a, b) => a + b, 0) / data.prices.length,
                precoMin: Math.min(...data.prices),
                precoMax: Math.max(...data.prices),
                qtdCompras: data.prices.length,
                ultimaCompra: data.lastDate,
            }))
            .sort((a, b) => a.precoMedio - b.precoMedio);
    }, [filteredHistory]);

    const handleAlertItemClick = (item: AlertaPreco) => {
        const catalogoItem = catalogoItens.find(ci => ci.id === item.id);
        if (catalogoItem) {
            setSelectedItem(catalogoItem);
        }
    };

    const handlePeriodChange = (period: '1M' | '3M' | '6M' | '1A' | 'Tudo') => {
        const today = new Date();
        let start = new Date();

        switch (period) {
            case '1M': start = subMonths(today, 1); break;
            case '3M': start = subMonths(today, 3); break;
            case '6M': start = subMonths(today, 6); break;
            case '1A': start = subYears(today, 1); break;
            case 'Tudo':
                setStartDate('');
                setEndDate('');
                return;
        }

        setStartDate(format(start, 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
    };

    const handleExportExcel = () => {
        if (filteredHistory.length === 0) return;
        const exportData = filteredHistory.map((row: HistoricoPrecoItem) => ({
            'Município': row.municipio,
            'Data': formatDate(row.data_lancamento),
            'Obra': row.obra_nome,
            'Fornecedor': row.fornecedor_nome || '--',
            'Quantidade': row.quantidade,
            'Preço Bruto': row.valor_bruto_unitario,
            'Desconto': row.valor_desconto_unitario,
            'Preço Líquido': row.valor_liquido_unitario,
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Histórico de Preços');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `memoria_preco_${selectedItem?.descricao || 'item'}.xlsx`);
    };

    return (
        <Container maxWidth="xl">
            <Typography variant="h4" sx={{ mb: 2 }}>
                Memória de Preço
            </Typography>

            {/* --- Alertas Panel --- */}
            <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Itens em Atenção</Typography>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Sensibilidade do Alerta</InputLabel>
                        <Select
                            value={alertThreshold}
                            label="Sensibilidade do Alerta"
                            onChange={(e) => setAlertThreshold(Number(e.target.value))}
                        >
                            <MenuItem value={5}>5% - Muito Sensível</MenuItem>
                            <MenuItem value={10}>10% - Padrão</MenuItem>
                            <MenuItem value={15}>15% - Moderado</MenuItem>
                            <MenuItem value={20}>20% - Pouco Sensível</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {loadingAlertas ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : (
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                        {/* Aumentos */}
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <TrendingUpIcon color="error" />
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }} color="error.main">
                                    Com Aumento ({alertas?.aumentos?.length || 0})
                                </Typography>
                            </Box>
                            {alertas && alertas.aumentos && alertas.aumentos.length > 0 ? (
                                <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
                                    {alertas.aumentos.map((item: AlertaPreco) => (
                                        <Paper
                                            key={`aumento-${item.id}`}
                                            variant="outlined"
                                            onClick={() => handleAlertItemClick(item)}
                                            sx={{
                                                p: 1.5,
                                                mb: 1,
                                                cursor: 'pointer',
                                                borderLeft: '4px solid',
                                                borderLeftColor: 'error.main',
                                                '&:hover': { boxShadow: 3, bgcolor: 'action.hover' },
                                                transition: 'all 0.2s',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Box sx={{ overflow: 'hidden', mr: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{item.descricao}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Último: {formatCurrency(item.ultimo_preco_liquido)} • Médio: {formatCurrency(item.preco_medio_liquido)}
                                                </Typography>
                                                <br />
                                                <Typography variant="caption" color="text.disabled">
                                                    {item.obra_nome}{item.fornecedor_nome && ` • ${item.fornecedor_nome}`}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={formatPercentage(item.variacao_percentual)}
                                                size="small"
                                                color="error"
                                                sx={{ fontWeight: 'bold', flexShrink: 0 }}
                                            />
                                        </Paper>
                                    ))}
                                </Box>
                            ) : <Alert severity="success" sx={{ mt: 1, borderRadius: 2 }}>Nenhum item com aumento relevante.</Alert>}
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

                        {/* Reduções */}
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <TrendingDownIcon color="success" />
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }} color="success.main">
                                    Com Redução ({alertas?.reducoes?.length || 0})
                                </Typography>
                            </Box>
                            {alertas && alertas.reducoes && alertas.reducoes.length > 0 ? (
                                <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
                                    {alertas.reducoes.map((item: AlertaPreco) => (
                                        <Paper
                                            key={`reducao-${item.id}`}
                                            variant="outlined"
                                            onClick={() => handleAlertItemClick(item)}
                                            sx={{
                                                p: 1.5,
                                                mb: 1,
                                                cursor: 'pointer',
                                                borderLeft: '4px solid',
                                                borderLeftColor: 'success.main',
                                                '&:hover': { boxShadow: 3, bgcolor: 'action.hover' },
                                                transition: 'all 0.2s',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Box sx={{ overflow: 'hidden', mr: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{item.descricao}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Último: {formatCurrency(item.ultimo_preco_liquido)} • Médio: {formatCurrency(item.preco_medio_liquido)}
                                                </Typography>
                                                <br />
                                                <Typography variant="caption" color="text.disabled">
                                                    {item.obra_nome}{item.fornecedor_nome && ` • ${item.fornecedor_nome}`}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={formatPercentage(item.variacao_percentual)}
                                                size="small"
                                                color="success"
                                                sx={{ fontWeight: 'bold', flexShrink: 0 }}
                                            />
                                        </Paper>
                                    ))}
                                </Box>
                            ) : <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>Nenhum item com redução relevante.</Alert>}
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* --- Analysis Panel --- */}
            <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Análise Detalhada por Item</Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Autocomplete
                        sx={{ flexGrow: 1, minWidth: 300 }}
                        options={catalogoItens}
                        getOptionLabel={(option) => option.descricao}
                        value={selectedItem}
                        onChange={(_, newValue) => setSelectedItem(newValue)}
                        renderInput={(params) => <TextField {...params} label="Selecione um Item" />}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                    />


                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Município</InputLabel>
                        <Select
                            value={selectedMunicipio}
                            label="Município"
                            onChange={(e) => setSelectedMunicipio(e.target.value)}
                        >
                            {municipios.map(m => (
                                <MenuItem key={m} value={m}>{m}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Obra</InputLabel>
                        <Select
                            value={selectedObraId}
                            label="Obra"
                            onChange={(e) => setSelectedObraId(e.target.value)}
                        >
                            <MenuItem value="Todos">Todas</MenuItem>
                            {obrasList.map(o => (
                                <MenuItem key={o.id} value={o.id}>{o.nome}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Fornecedor</InputLabel>
                        <Select
                            value={selectedFornecedorId}
                            label="Fornecedor"
                            onChange={(e) => setSelectedFornecedorId(e.target.value)}
                        >
                            <MenuItem value="Todos">Todos</MenuItem>
                            {fornecedoresList.map(f => (
                                <MenuItem key={f.id} value={f.id}>{f.nome}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Data Início"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: "9999-12-31" }}
                    />
                    <TextField
                        label="Data Fim"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: "9999-12-31" }}
                    />
                    <Button variant="outlined" onClick={() => {
                        setSelectedItem(null);
                        setSelectedMunicipio('Todos');
                        setSelectedObraId('Todos');
                        setSelectedFornecedorId('Todos');
                        setStartDate('');
                        setEndDate('');
                    }}>
                        Limpar
                    </Button>
                </Box>

                {loadingHistory && <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>}

                {!loadingHistory && priceHistory.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        {/* Metric Cards */}
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3, flexWrap: 'wrap' }}>
                            <Card sx={{ flex: '1 1 180px', background: 'linear-gradient(135deg, #667eea11, #764ba211)', borderRadius: 3 }}>
                                <CardContent>
                                    <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                                        {formatCurrency(metrics.ultimo)}
                                        {metrics.variacao > 0.1 ? <ArrowUpwardIcon color="error" /> :
                                            metrics.variacao < -0.1 ? <ArrowDownwardIcon color="success" /> :
                                                <RemoveIcon color="disabled" />}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Último Preço
                                        <Chip
                                            label={`${metrics.variacao > 0 ? '+' : ''}${metrics.variacao.toFixed(1)}%`}
                                            size="small"
                                            color={metrics.variacao > 0 ? 'error' : metrics.variacao < 0 ? 'success' : 'default'}
                                            variant="outlined"
                                            sx={{ ml: 1, fontWeight: 'bold', height: 20, fontSize: '0.7rem' }}
                                        />
                                    </Typography>
                                </CardContent>
                            </Card>
                            <Card sx={{ flex: '1 1 180px', background: 'linear-gradient(135deg, #f5f7fa, #c3cfe211)', borderRadius: 3 }}>
                                <CardContent>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{formatCurrency(metrics.medio)}</Typography>
                                    <Typography variant="body2" color="text.secondary">Preço Médio</Typography>
                                </CardContent>
                            </Card>
                            <Card sx={{ flex: '1 1 180px', background: 'linear-gradient(135deg, #ff616111, #ff935711)', borderRadius: 3 }}>
                                <CardContent>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'error.main' }}>{formatCurrency(metrics.maisAlto)}</Typography>
                                    <Typography variant="body2" color="text.secondary">Preço Mais Alto</Typography>
                                </CardContent>
                            </Card>
                            <Card sx={{ flex: '1 1 180px', background: 'linear-gradient(135deg, #00b09b11, #96c93d11)', borderRadius: 3 }}>
                                <CardContent>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>{formatCurrency(metrics.maisBaixo)}</Typography>
                                    <Typography variant="body2" color="text.secondary">Preço Mais Baixo</Typography>
                                </CardContent>
                            </Card>
                            {metrics.economiaPotencial > 0 && (
                                <Card sx={{ flex: '1 1 180px', background: 'linear-gradient(135deg, #11998e11, #38ef7d11)', border: '1px solid', borderColor: 'success.light', borderRadius: 3 }}>
                                    <CardContent>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }} color="success.main">
                                            <SavingsIcon fontSize="small" />
                                            {formatCurrency(metrics.economiaPotencial)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">Economia Potencial</Typography>
                                        <Typography variant="caption" color="text.disabled">se comprado pelo menor preço</Typography>
                                    </CardContent>
                                </Card>
                            )}
                        </Box>

                        {/* Fornecedor Ranking */}
                        {fornecedorRanking.length > 1 && (
                            <Paper sx={{ p: 3, mb: 4 }}>
                                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <EmojiEventsIcon color="warning" /> Ranking de Fornecedores
                                </Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell width={50}>#</TableCell>
                                                <TableCell>Fornecedor</TableCell>
                                                <TableCell align="right">Preço Médio</TableCell>
                                                <TableCell align="right">Menor Preço</TableCell>
                                                <TableCell align="right">Maior Preço</TableCell>
                                                <TableCell align="center">Compras</TableCell>
                                                <TableCell>Última Compra</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {fornecedorRanking.map((f, idx) => {
                                                const medalColors = ['#FFD700', '#C0C0C0', '#CD7F32'];
                                                const isBest = idx === 0;
                                                return (
                                                    <TableRow
                                                        key={f.id}
                                                        sx={{
                                                            backgroundColor: isBest ? 'rgba(76, 175, 80, 0.08)' : 'inherit',
                                                            '&:hover': { backgroundColor: 'action.hover' },
                                                        }}
                                                    >
                                                        <TableCell>
                                                            {idx < 3 ? (
                                                                <EmojiEventsIcon sx={{ color: medalColors[idx], fontSize: 20 }} />
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">{idx + 1}º</Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontWeight: isBest ? 'bold' : 'normal' }}>
                                                                {f.nome}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: isBest ? 'success.main' : 'inherit' }}>
                                                            {formatCurrency(f.precoMedio)}
                                                        </TableCell>
                                                        <TableCell align="right">{formatCurrency(f.precoMin)}</TableCell>
                                                        <TableCell align="right">{formatCurrency(f.precoMax)}</TableCell>
                                                        <TableCell align="center">{f.qtdCompras}</TableCell>
                                                        <TableCell>{formatDate(f.ultimaCompra)}</TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}

                        {/* Chart */}
                        <Paper sx={{ p: 3, mb: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">Variação do Preço Líquido</Typography>
                                <ButtonGroup size="small" variant="outlined" aria-label="text button group">
                                    <Button onClick={() => handlePeriodChange('1M')}>1M</Button>
                                    <Button onClick={() => handlePeriodChange('3M')}>3M</Button>
                                    <Button onClick={() => handlePeriodChange('6M')}>6M</Button>
                                    <Button onClick={() => handlePeriodChange('1A')}>1A</Button>
                                    <Button onClick={() => handlePeriodChange('Tudo')}>Tudo</Button>
                                </ButtonGroup>
                            </Box>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="data_lancamento" />
                                    <YAxis tickFormatter={(value) => formatCurrency(value as number)} />
                                    <Tooltip formatter={(value) => formatCurrency(value as number)} />
                                    <Legend />
                                    <ReferenceLine y={metrics.medio} label="Média" stroke="red" strokeDasharray="3 3" />
                                    <Line type="monotone" dataKey="valor_liquido_unitario" name="Preço Líquido" stroke="#8884d8" activeDot={{ r: 8 }} />
                                    <Line type="monotone" dataKey="tendencia" name="Tendência" stroke="#ff7300" strokeDasharray="5 5" dot={false} strokeWidth={2} />
                                </LineChart>
                            </ResponsiveContainer>
                        </Paper>

                        {/* Table */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Histórico de Compras</Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={handleExportExcel}
                            >
                                Exportar Excel
                            </Button>
                        </Box>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Município</TableCell>
                                        <TableCell>Data</TableCell>
                                        <TableCell>Obra</TableCell>
                                        <TableCell>Fornecedor</TableCell>
                                        <TableCell align="right">Qtd.</TableCell>
                                        <TableCell align="right">Preço Bruto</TableCell>
                                        <TableCell align="right">Desconto</TableCell>
                                        <TableCell align="right">Preço Líquido</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredHistory.map((row: HistoricoPrecoItem, index: number) => {
                                        const isHigh = row.valor_liquido_unitario > metrics.medio * 1.10;
                                        const isLow = row.valor_liquido_unitario < metrics.medio * 0.90;
                                        return (
                                            <TableRow key={index} sx={{ backgroundColor: isHigh ? '#ffebee' : isLow ? '#e8f5e9' : 'inherit' }}>
                                                <TableCell>{row.municipio}</TableCell>
                                                <TableCell>{formatDate(row.data_lancamento)}</TableCell>
                                                <TableCell>{row.obra_nome}</TableCell>
                                                <TableCell>{row.fornecedor_nome || '--'}</TableCell>
                                                <TableCell align="right">{row.quantidade}</TableCell>
                                                <TableCell align="right">{formatCurrency(row.valor_bruto_unitario)}</TableCell>
                                                <TableCell align="right">{formatCurrency(row.valor_desconto_unitario)}</TableCell>
                                                <TableCell align="right" sx={{ color: isHigh ? 'error.main' : isLow ? 'success.main' : 'inherit', fontWeight: 'bold' }}>
                                                    {formatCurrency(row.valor_liquido_unitario)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default MemoriaPrecoPage;
